import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import cs from "classnames";
import styles from "./styles.module.scss";
import { ChevronDownIcon } from "../NewOrderSelectApp/icons";
import { TimeDropdownProps } from "./types";

export const HOUR_OPTIONS = Array.from(
  { length: 24 },
  (_, h) => `${String(h).padStart(2, "0")}:00`,
);

// Menu height cap (keep in sync with .menu max-height) — used to decide
// whether the list still fits under the trigger.
const MENU_MAX_HEIGHT = 200;
const MENU_GAP = 4;

// Shown while there is no value — e.g. every hour of today is already past, so
// there is nothing to preselect. Keeps the trigger (and the menu, which takes
// its width) from collapsing to the bare chevron.
const TIME_PLACEHOLDER = "--:--";

/**
 * Time dropdown (Figma 106:3611 "Dropdown-select-time"): the value + chevron
 * trigger that opens an hour list. The list renders in a portal on <body>,
 * pinned to the trigger, so scrolling lists and clipping cards can't cut it.
 */
export const TimeDropdown: React.FC<TimeDropdownProps> = ({
  value,
  onChange,
  options = HOUR_OPTIONS,
  isOptionDisabled,
  disabled = false,
  size = "large",
  className,
  "aria-label": ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  // Fixed-position box of the open menu, measured from the trigger.
  const [menuBox, setMenuBox] = useState<React.CSSProperties | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const placeMenu = useCallback(() => {
    const trigger = rootRef.current?.getBoundingClientRect();
    if (!trigger) return;
    const spaceBelow = window.innerHeight - trigger.bottom;
    const openUp =
      spaceBelow < MENU_MAX_HEIGHT + MENU_GAP && trigger.top > spaceBelow;
    setMenuBox({
      left: trigger.left,
      width: trigger.width,
      ...(openUp
        ? {
            bottom: window.innerHeight - trigger.top + MENU_GAP,
            maxHeight: Math.min(MENU_MAX_HEIGHT, trigger.top - MENU_GAP * 2),
          }
        : {
            top: trigger.bottom + MENU_GAP,
            maxHeight: Math.min(MENU_MAX_HEIGHT, spaceBelow - MENU_GAP * 2),
          }),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    placeMenu();
    const handleOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        rootRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      )
        return;
      setOpen(false);
    };
    // A scroll anywhere moves the trigger — follow it. The menu's own list
    // scroll doesn't (and the capture listener sees it too), so skip it.
    const handleScroll = (event: Event) => {
      if (menuRef.current?.contains(event.target as Node)) return;
      placeMenu();
    };
    document.addEventListener("mousedown", handleOutside);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", placeMenu);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", placeMenu);
    };
  }, [open, placeMenu]);

  // Bring the current value into view once the menu is first placed. Not on
  // every reposition, or the list snaps back to the value while scrolling.
  const placed = menuBox !== null;
  useEffect(() => {
    if (!open || !placed) return;
    menuRef.current
      ?.querySelector<HTMLElement>('[aria-selected="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [open, placed]);

  return (
    <div ref={rootRef} className={cs(styles.root, className)}>
      <button
        type="button"
        className={cs(styles.trigger, styles[size], {
          [styles.triggerDisabled]: disabled,
        })}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className={cs(styles.value, { [styles.placeholder]: !value })}>
          {value || TIME_PLACEHOLDER}
        </span>
        <ChevronDownIcon className={styles.icon} />
      </button>
      {open &&
        menuBox &&
        createPortal(
          <ul
            ref={menuRef}
            className={cs(styles.menu, {
              [styles.menuCompact]: size === "medium",
            })}
            style={menuBox}
            role="listbox"
          >
            {options.map((time) => {
              const optionDisabled = isOptionDisabled?.(time) ?? false;
              return (
                <li key={time}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={time === value}
                    disabled={optionDisabled}
                    className={cs(styles.option, {
                      [styles.optionActive]: time === value,
                    })}
                    onClick={() => {
                      onChange(time);
                      setOpen(false);
                    }}
                  >
                    {time}
                  </button>
                </li>
              );
            })}
          </ul>,
          document.body,
        )}
    </div>
  );
};

export default TimeDropdown;
